// Etterretningsprodukt (INTEL) — kanonisk datamodell for arbeidsflyten
// data → informasjon → etterretning. Speiler NATO/STANAG 2511 «Admiralty
// Code»: kildepålitelighet A–F + informasjonstroverdighet 1–6. Et produkt kan
// kobles til stridsmeldinger (cases), graderes, og deles OPPOVER (distrikt/HV-
// stab) og SIDEVEIS (andre HV-områder). Modellen er forberedt for LLM-assistanse
// (deterministisk utkast nå; KI kan erstatte utkast-genereringen senere).
import { z } from 'zod';
/** Sikkerhetsgradering (norsk sikkerhetslov). */
export const Classification = z.enum(['UGRADERT', 'BEGRENSET', 'KONFIDENSIELT', 'HEMMELIG']);
/** Etterretningsdisiplin (hoveddisipliner, NATO/US-taksonomi). Ren enum (har
 * .options til UI); `.catch` påføres i produktskjemaet så ukjent/eldre verdi
 * faller til ALLKILDE i stedet for å forkaste produktet. */
export const Discipline = z.enum(['ALLKILDE', 'HUMINT', 'OSINT', 'GEOINT', 'IMINT', 'SIGINT', 'MASINT', 'TECHINT', 'FININT', 'MEDINT']);
export const DISCIPLINE_LABEL = {
    ALLKILDE: 'Allkilde',
    HUMINT: 'HUMINT — menneske',
    OSINT: 'OSINT — åpne kilder',
    GEOINT: 'GEOINT — geospatial',
    IMINT: 'IMINT — bilde (under GEOINT)',
    SIGINT: 'SIGINT — signal',
    MASINT: 'MASINT — måling/signatur',
    TECHINT: 'TECHINT — teknisk',
    FININT: 'FININT — finansiell',
    MEDINT: 'MEDINT — medisinsk (under TECHINT)',
};
/** Underdisipliner (forslag) per hoveddisiplin — fra NATO/US-taksonomien. Vises
 * som datalist i editoren; feltet er fritekst så nye kan legges til. */
export const SUBDISCIPLINES = {
    ALLKILDE: [],
    HUMINT: ['Kilde/agent', 'Patrulje', 'Krigsfange/detainee', 'Flyktning', 'Debrief', 'Attaché', 'NGO', 'FID/rådgiver'],
    OSINT: ['Internett/generelt', 'Sosiale medier', 'Nyheter/presse', 'Vitenskapelig/teknisk', 'Kringkasting'],
    GEOINT: ['IMINT — satellitt', 'IMINT — fly/drone', 'Kart/terreng', 'FMV (video)'],
    IMINT: ['Satellitt', 'Fly/drone', 'Bakkefoto'],
    SIGINT: ['COMINT — samband', 'ELINT — elektronisk', 'FISINT — telemetri'],
    MASINT: ['Elektro-optisk', 'Kjernefysisk', 'Radar', 'Geofysisk (akustisk/seismisk/magnetisk)', 'Materialer', 'Radiofrekvens'],
    TECHINT: ['Våpen/materiell', 'Miljøforhold'],
    FININT: ['Transaksjonsanalyse', 'Sanksjonsomgåelse'],
    MEDINT: ['Journal/helse', 'Epidemiologi'],
};
/** Kildepålitelighet (STANAG 2511, Admiralty Code A–F). */
export const Reliability = z.enum(['A', 'B', 'C', 'D', 'E', 'F']);
/** Informasjonstroverdighet (STANAG 2511, 1–6). */
export const Credibility = z.enum(['1', '2', '3', '4', '5', '6']);
export const RELIABILITY_LABEL = {
    A: 'A — Fullstendig pålitelig',
    B: 'B — Vanligvis pålitelig',
    C: 'C — Ganske pålitelig',
    D: 'D — Vanligvis ikke pålitelig',
    E: 'E — Upålitelig',
    F: 'F — Kan ikke vurderes',
};
export const CREDIBILITY_LABEL = {
    '1': '1 — Bekreftet',
    '2': '2 — Sannsynlig sann',
    '3': '3 — Muligens sann',
    '4': '4 — Tvilsom',
    '5': '5 — Usannsynlig',
    '6': '6 — Kan ikke vurderes',
};
export const CLASSIFICATION_COLOR = {
    UGRADERT: '#4bd15b',
    BEGRENSET: '#5b9bd5',
    KONFIDENSIELT: '#ff8a3d',
    HEMMELIG: '#ff5b5b',
};
/** Målgruppe for deling: oppover (stab) og/eller sideveis (andre områder). */
export const IntelAudienceSchema = z.object({
    /** Del oppover til distrikts-/HV-stab. */
    up: z.boolean().default(false),
    /** Distrikts-/områdekoder å dele sideveis til, f.eks. ['HV-01','HV-05']. */
    areas: z.array(z.string().max(64)).max(200).default([]),
});
export const IntelOrgSchema = z.object({
    district: z.string(),
    area: z.string().optional(),
});
/** IMINT-annotering: én bounding-box på et bilde (normaliserte koordinater
 * 0–1 så de skalerer uansett visningsstørrelse) + fritekst-annotasjon. */
export const ImintBoxSchema = z.object({
    x: z.number(),
    y: z.number(),
    w: z.number(),
    h: z.number(),
    label: z.string().max(200).default(''),
});
export const MAX_IMINT_BOXES = 200;
export const IntelProductSchema = z.object({
    id: z.string().max(128),
    title: z.string().max(500),
    body: z.string().max(20000).default(''),
    discipline: Discipline.catch('ALLKILDE').default('ALLKILDE'),
    /** Underdisiplin (COMINT/ELINT/IMINT-satellitt osv.) — fritekst m/forslag. */
    subDiscipline: z.string().max(64).optional(),
    classification: Classification.default('UGRADERT'),
    reliability: Reliability.default('F'),
    credibility: Credibility.default('6'),
    author: z.string(),
    org: IntelOrgSchema,
    tsMs: z.number(),
    updatedMs: z.number().default(0),
    audience: IntelAudienceSchema.default({ up: false, areas: [] }),
    /** Koblede stridsmeldinger (case-id-er) som produktet bygger på. */
    linkedReportIds: z.array(z.string().max(128)).max(500).default([]),
    /** Valgfri geoposisjon (fokusområde). Bundet til gyldige grader (STIX/GeoJSON-krav). */
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    /**
     * Sted som MGRS (kompakt form, som `latLonToMgrs` gir — formatér ved visning).
     * UX-funn 8 (J3-rapport 2026-08-04): et etterretningsprodukt uten STED kan
     * ikke plottes, sammenlignes eller avgrenses — rom/tid er selve grunnenheten
     * i etterretning. Feltet er VALGFRITT i kontrakten fordi produkter fra før
     * feltet fantes fortsatt skal kunne vises (bakoverkompatibelt); at NYE
     * produkter må ha sted, håndheves i editoren — ikke ved å forkaste gamle data.
     */
    mgrs: z.string().max(32).optional(),
    /** Gyldighetsvindu (ms epoch): NÅR vurderingen gjelder fra/til. Samme
     * begrunnelse som `mgrs` — valgfritt for bakoverkompatibilitet. */
    gjelderFraMs: z.number().optional(),
    gjelderTilMs: z.number().optional(),
    /** IMINT: bilde-URL + bounding-box-annoteringer. */
    imageUrl: z.string().max(2048).optional(),
    boxes: z.array(ImintBoxSchema).max(MAX_IMINT_BOXES).default([]),
});
export const MAX_INTEL = 2000;
export function parseIntel(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const item of raw.slice(0, MAX_INTEL)) {
        const r = IntelProductSchema.safeParse(item);
        if (r.success)
            out.push(r.data);
    }
    return out;
}
/** Én enkelt intel — validér for collab-mottak (ett produkt om gangen). */
export function parseIntelOne(raw) {
    const r = IntelProductSchema.safeParse(raw);
    return r.success ? r.data : null;
}
/** Kort Admiralty-kode, f.eks. «B2». */
export function admiraltyCode(p) {
    return `${p.reliability}${p.credibility}`;
}
/**
 * Er et gyldighetsvindu konsistent? Åpne ender er lovlige (bare fra, bare til,
 * ingen av dem) — det ULOVLIGE er et vindu som slutter før det begynner.
 * Håndheves i editoren ved lagring, IKKE i skjemaet: et mottatt produkt med
 * rart vindu skal vises (og kunne rettes), ikke forsvinne stille fra lista.
 */
export function gyldigTidsrom(fraMs, tilMs) {
    return fraMs === undefined || tilMs === undefined || tilMs >= fraMs;
}
/** Sist-vinner-fletting (produkter eies/redigeres av forfatter). */
export function mergeIntel(a, b) {
    return b.updatedMs >= a.updatedMs ? b : a;
}
//# sourceMappingURL=intel.js.map